/**
 * Safe Storage Utility
 *
 * Handles localStorage/sessionStorage access with graceful fallback
 * for browsers with tracking prevention enabled (Safari, Firefox, etc.)
 */

type StorageType = 'localStorage' | 'sessionStorage'

class SafeStorage {
  private memoryStore: Map<string, string> = new Map()
  private isStorageAvailable: { [key: string]: boolean } = {}

  /**
   * Check if a storage type is available
   */
  private checkStorageAvailability(type: StorageType): boolean {
    if (this.isStorageAvailable[type] !== undefined) {
      return this.isStorageAvailable[type]
    }

    try {
      const storage = window[type]
      const testKey = '__storage_test__'
      storage.setItem(testKey, 'test')
      storage.removeItem(testKey)
      this.isStorageAvailable[type] = true
      return true
    } catch (error) {
      // Storage not available, using in-memory fallback
      this.isStorageAvailable[type] = false
      return false
    }
  }

  /**
   * Get an item from storage
   */
  getItem(key: string, type: StorageType = 'localStorage'): string | null {
    if (this.checkStorageAvailability(type)) {
      try {
        return window[type].getItem(key)
      } catch (error) {
        // Error reading from storage
      }
    }

    // Fallback to memory store
    return this.memoryStore.get(`${type}:${key}`) || null
  }

  /**
   * Set an item in storage
   */
  setItem(key: string, value: string, type: StorageType = 'localStorage'): void {
    if (this.checkStorageAvailability(type)) {
      try {
        window[type].setItem(key, value)
        return
      } catch (error) {
        // Error writing to storage
      }
    }

    // Fallback to memory store
    this.memoryStore.set(`${type}:${key}`, value)
  }

  /**
   * Remove an item from storage
   */
  removeItem(key: string, type: StorageType = 'localStorage'): void {
    if (this.checkStorageAvailability(type)) {
      try {
        window[type].removeItem(key)
        return
      } catch (error) {
        // Error removing from storage
      }
    }

    // Fallback to memory store
    this.memoryStore.delete(`${type}:${key}`)
  }

  /**
   * Clear all items from storage
   */
  clear(type: StorageType = 'localStorage'): void {
    if (this.checkStorageAvailability(type)) {
      try {
        window[type].clear()
        return
      } catch (error) {
        // Error clearing storage
      }
    }

    // Clear memory store for this type
    const prefix = `${type}:`
    const keysToDelete = Array.from(this.memoryStore.keys()).filter(k => k.startsWith(prefix))
    keysToDelete.forEach(k => this.memoryStore.delete(k))
  }

  /**
   * Get all keys from storage
   */
  getAllKeys(type: StorageType = 'localStorage'): string[] {
    if (this.checkStorageAvailability(type)) {
      try {
        return Object.keys(window[type])
      } catch (error) {
        // Error getting keys from storage
      }
    }

    // Get keys from memory store
    const prefix = `${type}:`
    return Array.from(this.memoryStore.keys())
      .filter(k => k.startsWith(prefix))
      .map(k => k.substring(prefix.length))
  }

  /**
   * Check if storage is available
   */
  isAvailable(type: StorageType = 'localStorage'): boolean {
    return this.checkStorageAvailability(type)
  }
}

// Export singleton instance
export const safeStorage = new SafeStorage()

// Export convenience functions
export const safeLocalStorage = {
  getItem: (key: string) => safeStorage.getItem(key, 'localStorage'),
  setItem: (key: string, value: string) => safeStorage.setItem(key, value, 'localStorage'),
  removeItem: (key: string) => safeStorage.removeItem(key, 'localStorage'),
  clear: () => safeStorage.clear('localStorage'),
  getAllKeys: () => safeStorage.getAllKeys('localStorage'),
  isAvailable: () => safeStorage.isAvailable('localStorage'),
}

export const safeSessionStorage = {
  getItem: (key: string) => safeStorage.getItem(key, 'sessionStorage'),
  setItem: (key: string, value: string) => safeStorage.setItem(key, value, 'sessionStorage'),
  removeItem: (key: string) => safeStorage.removeItem(key, 'sessionStorage'),
  clear: () => safeStorage.clear('sessionStorage'),
  getAllKeys: () => safeStorage.getAllKeys('sessionStorage'),
  isAvailable: () => safeStorage.isAvailable('sessionStorage'),
}

export default safeStorage
